import React, { useState, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    OutlinedInput,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Paper,
    TextField,
    Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { EquipmentRequestEntity, FileStoreEntity } from 'graphql/graphQlApiHooks';
import UploaderService from 'services/UplodaService';
import { useSnackbar } from 'notistack';
import Iconify from 'components/Iconify';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

interface EquipmentQuantity {
    equipmentRequestId: string;
    equipmentName: string;
    originalQuantity: number;
    newQuantity: number;
    isEditing: boolean;
}

interface Props {
    equipmentRequests: EquipmentRequestEntity[];
    onClose: () => void;
    onAccept: (acceptanceCertificateId: string, equipmentQuantities: Array<{equipmentRequestId: string, quantity: number, name?: string}>) => void;
    loading?: boolean;
}

export const BulkEquipmentAcceptanceDialog: React.FC<Props> = ({
    equipmentRequests,
    onClose,
    onAccept,
    loading = false
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const [acceptanceFile, setAcceptanceFile] = useState<FileStoreEntity | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    
    const [equipmentQuantities, setEquipmentQuantities] = useState<EquipmentQuantity[]>(() => {
        return equipmentRequests.map(request => ({
            equipmentRequestId: request.id,
            equipmentName: request.name,
            originalQuantity: request.count || 0,
            newQuantity: request.count || 0,
            isEditing: false
        }));
    });

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setUploadingFile(true);
        try {
            const uploadedFiles = await UploaderService.uploadFiles(event as any, {
                onUploadProgress: (progressEvent: any) => {
                }
            });
            if (uploadedFiles && uploadedFiles.length > 0) {
                setAcceptanceFile(uploadedFiles[0]);
                enqueueSnackbar('Файл акта приемки успешно загружен', { variant: 'success' });
            }
        } catch (error) {
            enqueueSnackbar('Ошибка при загрузке файла', { variant: 'error' });
            console.error('File upload error:', error);
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleQuantityChange = (equipmentRequestId: string, newQuantity: number) => {
        setEquipmentQuantities(prev => 
            prev.map(item => 
                item.equipmentRequestId === equipmentRequestId 
                    ? { ...item, newQuantity }
                    : item
            )
        );
    };

    const handleNameChange = (equipmentRequestId: string, newName: string) => {
        setEquipmentQuantities(prev => 
            prev.map(item => 
                item.equipmentRequestId === equipmentRequestId 
                    ? { ...item, equipmentName: newName }
                    : item
            )
        );
    };

    const toggleEditMode = (equipmentRequestId: string) => {
        setEquipmentQuantities(prev => 
            prev.map(item => 
                item.equipmentRequestId === equipmentRequestId 
                    ? { ...item, isEditing: !item.isEditing }
                    : item
            )
        );
    };

    const handleDeleteRequest = (equipmentRequestId: string) => {
        setEquipmentQuantities(prev => 
            prev.filter(item => item.equipmentRequestId !== equipmentRequestId)
        );
    };

    const handleRemoveFile = () => {
        setAcceptanceFile(null);
    };

    const handleSubmit = async () => {
        try {
            const quantities = equipmentQuantities
                .filter(item => item.newQuantity > 0)
                .map(item => {
                    const originalRequest = equipmentRequests.find(req => req.id === item.equipmentRequestId);
                    const isNameChanged = originalRequest && originalRequest.name !== item.equipmentName;
                    
                    return {
                        equipmentRequestId: item.equipmentRequestId,
                        quantity: item.newQuantity,
                        ...(isNameChanged && { name: item.equipmentName })
                    };
                });

            onAccept(acceptanceFile?.id || null, quantities);
            
        } catch (error) {
            enqueueSnackbar(`Ошибка при обработке заявок: ${error?.message}`, { variant: "error" });
            console.error("Ошибка при обработке заявок", error);
        }
    };

    return (
        <>
            <DialogTitle>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Приемка оборудования
                </Typography>
                <Typography className='text-sm' variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Прием оборудования для {equipmentQuantities.length} {
                        equipmentQuantities.length === 1 ? 'заявки' : 
                        equipmentQuantities.length > 1 && equipmentQuantities.length < 5 ? 'заявок' : 
                        'заявок'
                    }
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Акт приемки оборудования 
                        </Typography>
                        
                        {!acceptanceFile ? (
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<Iconify icon="material-symbols:upload" />}
                                disabled={uploadingFile}
                                fullWidth
                                sx={{ py: 2 }}
                            >
                                {uploadingFile ? 'Загрузка...' : 'Загрузить акт приемки'}
                                <VisuallyHiddenInput
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileUpload}
                                />
                            </Button>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Iconify icon="material-symbols:description" />
                                        <Typography variant="body2">
                                            {acceptanceFile.name}
                                        </Typography>
                                    </Box>
                                    <IconButton 
                                        size="small" 
                                        onClick={handleRemoveFile}
                                        color="error"
                                    >
                                        <Iconify icon="material-symbols:delete" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Количество принятого оборудования
                        </Typography>
                        
                        {equipmentQuantities.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Наименование оборудования</TableCell>
                                            <TableCell align="center">Заявленное количество</TableCell>
                                            <TableCell align="center">Принятое количество</TableCell>
                                            <TableCell align="center">Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {equipmentQuantities.map((item) => (
                                            <TableRow key={item.equipmentRequestId}>
                                                <TableCell>
                                                    {item.isEditing ? (
                                                        <TextField
                                                            value={item.equipmentName}
                                                            onChange={(e) => handleNameChange(item.equipmentRequestId, e.target.value)}
                                                            onBlur={() => toggleEditMode(item.equipmentRequestId)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    toggleEditMode(item.equipmentRequestId);
                                                                }
                                                            }}
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                                                {item.equipmentName}
                                                            </Typography>
                                                            <Tooltip title="Редактировать название">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => toggleEditMode(item.equipmentRequestId)}
                                                                >
                                                                    <Iconify icon="material-symbols:edit" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.originalQuantity}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <FormControl size="small" sx={{ minWidth: 80 }}>
                                                        <OutlinedInput
                                                            value={item.newQuantity}
                                                            onChange={(e) => handleQuantityChange(item.equipmentRequestId, parseInt(e.target.value) || 0)}
                                                            type="number"
                                                            inputProps={{ min: 0 }}
                                                        />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Удалить из таблицы">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleDeleteRequest(item.equipmentRequestId)}
                                                            color="error"
                                                        >
                                                            <Iconify icon="material-symbols:delete" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Все заявки были удалены из таблицы.
                                </Typography>
                            </Paper>
                        )}
                    </Box>

                    <Alert severity="info">
                        Загрузите акт приемки оборудования и укажите фактическое количество принятого оборудования для каждой позиции.
                    </Alert>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Отмена
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    disabled={equipmentQuantities.length === 0}
                >
                    Подтвердить принятие
                </LoadingButton>
            </DialogActions>
        </>
    );
};